import { type FC, useCallback, useEffect, useState } from "react";
import {
	ATTRIBUTE_CATEGORIES,
	AttributeApi,
	type AttributeCategory,
	type AttributeListItem,
} from "@/api/AttributeApi";
import { formatAttributeName } from "@/utils/AttributeHelper";
import IndexCard from "../../components/Card/IndexCard";
import LoadingPage from "../LoadingPage";

interface AttributePageProps {
	category: string;
	active?: boolean;
}

const styles = {
	pageContainer: "p-4 w-full my-auto",
	pageTitle: "text-2xl font-medium mb-4",
	gridContainer: "grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4",
	contentWrapper: "mx-auto w-full max-w-[1800px]",
	tabSurface: "w-full rounded-xl border border-slate-200 bg-white p-4 pt-2 shadow-sm sm:p-6 md:p-8",
	sectionTitle: "text-2xl font-medium mt-0 mb-4",
	attributesContainer:
		"mx-auto flex w-full flex-wrap justify-start gap-4 overflow-x sm:overflow-visible",
	overallAttributesContainer: "flex w-full flex-col gap-6",
	archivedSection: "mt-6 w-full border-t border-red-200 pt-6",
	archivedTitle: "mb-3 text-xl font-medium text-red-800",
	archivedCardsContainer:
		"mx-auto flex w-full flex-wrap justify-start gap-4 overflow-x sm:overflow-visible",
	archivedCardWrapper: "rounded-lg border border-red-300 bg-white p-2",
	archivedBadge:
		"ml-4 mt-3 inline-block rounded-full border border-red-300 bg-red-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-red-800",
	singleTabContainer: "w-full space-y-2 pt-2",
};

const ARCHIVED_ENVIRONMENTAL_ATTRIBUTES = ["Commodity Forest Loss", "Deforestation Vulnerability"];
const ARCHIVED_BADGE_TEXT = "Archived";
const VENDOR_NAME = "Verisk Maplecroft";
const NO_INDICES_CATEGORY_MESSAGE = "No indices available for this category.";
const NO_INDICES_OVERALL_MESSAGE =
	"No indices available for environment and human rights attributes.";

const getAttributesByCategory = async (
	category: string,
	active: boolean,
): Promise<AttributeListItem[]> => {
	if (category === ATTRIBUTE_CATEGORIES.OVERALL) {
		const [environmentalAttributes, humanRightsAttributes] = await Promise.all([
			AttributeApi.getAttributeList(ATTRIBUTE_CATEGORIES.ENVIRONMENTAL, active),
			AttributeApi.getAttributeList(ATTRIBUTE_CATEGORIES.HUMANRIGHTS, active),
		]);
		return [...environmentalAttributes, ...humanRightsAttributes];
	}
	return AttributeApi.getAttributeList(category as AttributeCategory, active);
};

const getPageTitle = (category: string) => {
	switch (category) {
		case ATTRIBUTE_CATEGORIES.ENVIRONMENTAL:
			return "Environmental Indices";
		case ATTRIBUTE_CATEGORIES.HUMANRIGHTS:
			return "Human Rights Indices";
		default:
			return "";
	}
};

const AttributePage: FC<AttributePageProps> = ({ category, active = true }) => {
	const [attributes, setAttributes] = useState<AttributeListItem[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<boolean>(false);

	const fetchAttributes = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const attributeList = await getAttributesByCategory(category, active);
			setAttributes(attributeList);
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	}, [category, active]);

	useEffect(() => {
		fetchAttributes();
	}, [fetchAttributes]);

	const renderAttributes = (filteredAttributes: AttributeListItem[], title: string) => (
		<section>
			{title && <h1 className={styles.sectionTitle}>{title}</h1>}
			<div className={styles.attributesContainer}>
				{filteredAttributes.map((item) => (
					<IndexCard
						key={item.attributeId}
						name={formatAttributeName(item.attributeName)}
						year={item.calendarYear}
						active={item.isActive}
						vendor={VENDOR_NAME}
					/>
				))}
			</div>
		</section>
	);

	const renderArchivedEnvironmentalAttributes = (filteredAttributes: AttributeListItem[]) => {
		if (category !== ATTRIBUTE_CATEGORIES.ENVIRONMENTAL || filteredAttributes.length === 0) {
			return null;
		}

		return (
			<div className={styles.archivedSection}>
				{/* <h2 className={styles.archivedTitle}>{ARCHIVED_INDICES_TITLE}</h2> */}
				<div className={styles.archivedCardsContainer}>
					{ARCHIVED_ENVIRONMENTAL_ATTRIBUTES.map((archivedAttribute) => (
						<div key={archivedAttribute} className={styles.archivedCardWrapper}>
							<span className={styles.archivedBadge}>{ARCHIVED_BADGE_TEXT}</span>
							<IndexCard name={archivedAttribute} year={2026} vendor={VENDOR_NAME} />
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderOverallAttributes = () => (
		<div className={styles.overallAttributesContainer}>
			{renderAttributes(
				attributes.filter((attr) => attr.categoryName === "Environmental"),
				"Environmental Indices",
			)}
			{renderAttributes(
				attributes.filter((attr) => attr.categoryName === "Human Rights"),
				"Human Rights Indices",
			)}
		</div>
	);

	const showError = () => (
		<div className="flex flex-col items-center justify-center pt-[100px]">
			<p className="text-red-500 text-lg font-medium mb-2">Unable to load attribute data</p>
			<p className="text-gray-600">
				There was a problem retrieving the attribute information. Please try again later.
			</p>
			<button
				type="button"
				className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
				onClick={() => {
					void fetchAttributes();
				}}
			>
				Retry
			</button>
		</div>
	);

	return (
		<div className={styles.pageContainer}>
			{loading ? (
				<LoadingPage title="Loading attributes..." />
			) : error ? (
				showError()
			) : (
				<div className={styles.contentWrapper}>
					<div className="flex w-full justify-center">
						{category === ATTRIBUTE_CATEGORIES.OVERALL ? (
							attributes.length > 0 ? (
								<section className={styles.tabSurface}>
									<div className={styles.singleTabContainer}>
										<h1 className={styles.sectionTitle}>{getPageTitle(category)}</h1>
										{renderOverallAttributes()}
									</div>
								</section>
							) : (
								<p>{NO_INDICES_OVERALL_MESSAGE}</p>
							)
						) : attributes.length > 0 ? (
							<section className={styles.tabSurface}>
								<div className={styles.singleTabContainer}>
									{renderAttributes(attributes, getPageTitle(category))}
									{renderArchivedEnvironmentalAttributes(attributes)}
								</div>
							</section>
						) : (
							<p>{NO_INDICES_CATEGORY_MESSAGE}</p>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default AttributePage;
